import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getMintSpaceById } from "../api/mintSpace";
import SpaceGate from "../components/SpaceGate";
import { useAuth } from "./AuthContext";
import { useGuardedNavigate } from "./AuthModalContext";

const SpaceGateContext = createContext(null);

/**
 * Opens a space from anywhere: people in it go straight in, anyone else gets the join card
 * over the page they're on. Pass the space or its id.
 */
export function useOpenSpace() {
  return useContext(SpaceGateContext);
}

export function SpaceGateProvider({ isDark, setToast, children }) {
  const { isAuthenticated } = useAuth();
  const navigate = useGuardedNavigate();
  const { pathname } = useLocation();
  const [gate, setGate] = useState(null);
  const latestOpen = useRef(0);

  // Leaving the page drops the card, and an answer still on its way.
  useEffect(() => {
    latestOpen.current += 1;
    setGate(null);
  }, [pathname]);

  const openSpace = useCallback(
    async (space) => {
      const id = space?._id || space;
      const path = `/mint-space/${id}`;
      if (!isAuthenticated) {
        navigate(path);
        return;
      }
      latestOpen.current += 1;
      const thisOpen = latestOpen.current;
      try {
        const { data } = await getMintSpaceById(id);
        if (thisOpen !== latestOpen.current) return;
        if (!data || data.isOwner || data.isMember || data.isModerator) navigate(path);
        else setGate(data);
      } catch {
        // The space's own page knows how to say it's gone.
        if (thisOpen === latestOpen.current) navigate(path);
      }
    },
    [isAuthenticated, navigate]
  );

  return (
    <SpaceGateContext.Provider value={openSpace}>
      {children}
      {gate && (
        <SpaceGate
          space={gate}
          isDark={isDark}
          setToast={setToast}
          onClose={() => setGate(null)}
          onJoined={() => navigate(`/mint-space/${gate._id}`)}
        />
      )}
    </SpaceGateContext.Provider>
  );
}
